import React from 'react'
import { Divider, Flex, Text } from '@chilipiper/design-system'
import { getUserRole } from '@chilipiper/permissions'
import { Badge, IconButton, NewTabButton, IconTooltip } from '@chilipiper/design-system/src/new'
import { IconKey } from '@chilipiper/icons/figma-generated/src/icon'
import { useShouldNudgeToClaimAdminCenter } from '../../hooks/useShouldNudgeToClaimAdminCenter'
import { useTenantDetails } from '../../api/tenant'
import { useOrganizationDetails, useTenant, useUser } from '../../api/workspace'

export const AdminCenterLinkWithLicenseRequest = ({ isExpanded }: { isExpanded: boolean }) => {
  const { adminCenterUrl, shouldNudgeToClaimAdminCenter } = useShouldNudgeToClaimAdminCenter()
  const { data: tenantDetails } = useTenantDetails()
  const { data: orgData } = useOrganizationDetails()
  const { data: tenantData } = useTenant()
  const { data: userData } = useUser()
  const { data: adminData } = useUser({
    enabled: Boolean(orgData?.adminId),
    userId: orgData?.adminId ?? '',
  })

  const isOrgTenant = tenantDetails?.tenantInfo.type === 'Organization'
  const isTenantUnclaimed = tenantData?.claimStatus === 'Unclaimed'

  const isLoading = !tenantDetails || !tenantData || !userData

  if (isLoading) {
    return null
  }

  const role = getUserRole(userData)
  const isAdmin = userData?.isSuperAdmin
  const isWorkspaceManager = role === 'WorkspaceManager'

  const adminCenterLinkText = isTenantUnclaimed
    ? 'Set up your Organization'
    : 'Go to Command Center'

  if (isExpanded) {
    return (
      <>
        <Divider />
        <Flex flexDirection='column' py={4} px={5} bg='bg/moderate' gap={3}>
          <Flex alignItems='center' justifyContent='space-between'>
            <Text textStyle='heading-xs' color='fg/major' whiteSpace='nowrap'>
              {isOrgTenant && !isTenantUnclaimed
                ? (orgData?.name ?? 'Organization')
                : 'Personal Account'}
            </Text>
            {!isTenantUnclaimed && (
              <>
                {!isAdmin ? (
                  <Badge label='Active' variant='inform' />
                ) : (
                  <IconTooltip
                    content={`For any questions with your license contact ${
                      adminData?.email ?? 'your admin'
                    }`}
                    variant='inform'
                    size={4}
                    placement='right'
                  />
                )}
              </>
            )}
          </Flex>
          {shouldNudgeToClaimAdminCenter || isAdmin || isWorkspaceManager ? (
            <NewTabButton
              variant='minor'
              href={adminCenterUrl ?? ''}
              isDisabled={!adminCenterUrl}
              data-id='left-nav-admin-center'
              isFullWidth
            >
              {adminCenterLinkText}
            </NewTabButton>
          ) : (
            <Text textStyle='body-multiline-tight' color='fg/moderate'>
              {isOrgTenant && !isTenantUnclaimed
                ? `You are a part of ${orgData?.name ?? 'an'} organization.`
                : `You are using free version of ChiliCal`}
            </Text>
          )}
        </Flex>
      </>
    )
  }

  if (shouldNudgeToClaimAdminCenter || isAdmin || isWorkspaceManager) {
    return (
      <IconButton
        as='a'
        href={adminCenterUrl ?? ''}
        isDisabled={!adminCenterUrl}
        target='_blank'
        mx={3}
        my={4}
        icon={IconKey}
        label={adminCenterLinkText}
        size={8}
        variant='minor'
      />
    )
  }

  return null
}
